import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { PageHeader } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, ArrowRight } from "lucide-react";
import { SiretInput } from "@/components/SiretInput";
import { validateSiret } from "@/lib/siret";

export default function AdminEntreprises() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", siret: "", chef_nom: "", chef_prenom: "", chef_email: "", chef_telephone: "", chef_password: "" });
  const [creating, setCreating] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/entreprises");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const siretCheck = validateSiret(form.siret);
    if (siretCheck.state !== "valid") { toast.error(siretCheck.message || "SIRET invalide"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/entreprises", { ...form, siret: siretCheck.cleaned });
      setCreatedInfo(data);
      setForm({ name: "", siret: "", chef_nom: "", chef_prenom: "", chef_email: "", chef_telephone: "", chef_password: "" });
      toast.success("Entreprise créée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const statusPill = (s) => {
    const map = {
      active: ["status-active", "Active"],
      pending_payment: ["status-pending", "Paiement"],
      blocked: ["status-blocked", "Bloquée"],
      inactive: ["status-inactive", "Inactive"],
    };
    const [cls, label] = map[s] || ["status-inactive", s];
    return <span className={`status-pill ${cls}`}>{label}</span>;
  };

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Entreprises"
        title="Gestion des entreprises"
        sub="Créez, activez, bloquez et surveillez le statut de paiement de chaque entreprise."
        right={
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setCreatedInfo(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="admin-create-entreprise-btn" className="h-11 bg-[#002FA7] hover:bg-[#002175] text-white rounded-md font-semibold">
                <Plus className="h-4 w-4 mr-2" /> Créer une entreprise
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-black tracking-tight">Nouvelle entreprise</DialogTitle>
              </DialogHeader>
              {createdInfo ? (
                <div className="space-y-4">
                  <div className="p-4 bg-[#ECFDF5] border border-[#A7F3D0] text-sm">
                    Entreprise créée. Transmettez ces identifiants au chef d'entreprise.
                  </div>
                  <div className="p-4 border border-[#DEE2E6] bg-[#F8F9FA] font-mono text-sm space-y-2" data-testid="admin-created-info">
                    <div><span className="text-[#868E96]">Code entreprise :</span> <span className="font-bold">{createdInfo.entreprise.code}</span></div>
                    <div><span className="text-[#868E96]">Chef :</span> {createdInfo.chef.prenom} {createdInfo.chef.nom}</div>
                    <div><span className="text-[#868E96]">Mot de passe :</span> <span className="font-bold">{createdInfo.generated_password}</span></div>
                  </div>
                  <Button onClick={() => { setOpen(false); setCreatedInfo(null); }} className="w-full bg-[#0A0A0A] text-white">Terminer</Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="admin-entreprise-form">
                  <Field label="Nom de l'entreprise" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="input-name" required />
                  <SiretInput label="SIRET (14 chiffres)" value={form.siret} onChange={(v) => setForm({ ...form, siret: v })} testid="input-siret" required labelClass="text-xs uppercase tracking-[0.2em] font-semibold" />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Nom du chef" value={form.chef_nom} onChange={(v) => setForm({ ...form, chef_nom: v })} testid="input-chef-nom" required />
                    <Field label="Prénom du chef" value={form.chef_prenom} onChange={(v) => setForm({ ...form, chef_prenom: v })} testid="input-chef-prenom" required />
                  </div>
                  <Field label="Email (optionnel)" type="email" value={form.chef_email} onChange={(v) => setForm({ ...form, chef_email: v })} testid="input-chef-email" />
                  <Field label="Téléphone du chef *" type="tel" value={form.chef_telephone} onChange={(v) => setForm({ ...form, chef_telephone: v })} testid="input-chef-telephone" required />
                  <Field label="Mot de passe initial" type="password" value={form.chef_password} onChange={(v) => setForm({ ...form, chef_password: v })} testid="input-chef-password" required />
                  <DialogFooter>
                    <Button type="submit" disabled={creating} data-testid="admin-entreprise-submit" className="bg-[#002FA7] hover:bg-[#002175] text-white">
                      {creating ? "Création…" : "Créer l'entreprise"}
                    </Button>
                  </DialogFooter>
                </form>
              )}
            </DialogContent>
          </Dialog>
        }
      />

      <div className="p-6 lg:p-10">
        <div className="bg-white border border-[#DEE2E6] overflow-hidden" data-testid="admin-entreprises-list">
          <div className="grid grid-cols-12 gap-4 p-4 text-xs uppercase tracking-[0.2em] font-semibold bg-[#F8F9FA] border-b border-[#DEE2E6]">
            <div className="col-span-3">Entreprise</div>
            <div className="col-span-3">Identifiants</div>
            <div className="col-span-2">Chef</div>
            <div className="col-span-1">Salariés</div>
            <div className="col-span-2">Statut</div>
            <div className="col-span-1 text-right"></div>
          </div>
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#868E96]">Aucune entreprise.</div>
          ) : (
            items.map((e) => (
              <div key={e.id} className="grid grid-cols-12 gap-4 p-4 border-b border-[#DEE2E6] items-center hover:bg-[#F8F9FA] transition-colors" data-testid={`entreprise-row-${e.code}`}>
                <div className="col-span-3 font-display font-bold">{e.name}</div>
                <div className="col-span-3 font-mono text-[11px] leading-tight" data-testid={`entreprise-ids-${e.code}`}>
                  <div className="text-[#059669] font-bold">{e.pag_cli || "PAG-CLI —"}</div>
                  <div className="text-[#002FA7]">{e.code}</div>
                </div>
                <div className="col-span-2 text-sm">{e.chef ? `${e.chef.prenom} ${e.chef.nom}` : "—"}</div>
                <div className="col-span-1 text-sm">{e.salaries_count}</div>
                <div className="col-span-2">{statusPill(e.status)}</div>
                <div className="col-span-1 text-right">
                  <Link to={`/admin/entreprises/${e.id}`} data-testid={`entreprise-open-${e.code}`} className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.2em] hover:text-[#002FA7]">
                    Gérer <ArrowRight className="h-3 w-3 ml-1" />
                  </Link>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">{label}</Label>
      <Input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 border-[#DEE2E6] focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
        required={required}
      />
    </div>
  );
}
